import React, { useEffect } from "react";

/**
 * Injects merchant-supplied HTML snippets into <head>, top of <body>, and
 * bottom of <body>. Used for analytics (GA, GTM, FB Pixel), chat widgets,
 * search-console verification, etc.
 *
 * Why imperative DOM manipulation rather than React Helmet?
 *   - These are arbitrary HTML strings (often <script src=...> + inline JS),
 *     and Helmet's <script> children aren't great for raw multi-tag HTML.
 *   - Each slot needs to be a "managed region" we fully replace on update,
 *     so we wrap each in a comment-marker container we can target.
 *
 * Slots are no-ops when the merchant hasn't configured the corresponding
 * snippet, so this component has zero overhead in default deployments.
 */
const HEAD_MARKER = "rieave-tracking-head";
const BODY_OPEN_MARKER = "rieave-tracking-body-open";
const BODY_CLOSE_MARKER = "rieave-tracking-body-close";

const ensureContainer = (parent, markerId, position = "append") => {
  let el = document.getElementById(markerId);
  if (!el) {
    el = document.createElement("div");
    el.id = markerId;
    el.style.display = "contents";
    if (position === "prepend" && parent.firstChild) {
      parent.insertBefore(el, parent.firstChild);
    } else {
      parent.appendChild(el);
    }
  }
  return el;
};

const replaceContent = (container, html) => {
  // Clear the container completely
  while (container.firstChild) container.removeChild(container.firstChild);
  if (!html || !html.trim()) return;
  // Use a template element to safely parse the HTML, then move all children
  // (including <script>, <noscript>, <meta>) into the container so the browser
  // executes scripts.
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const frag = tpl.content;
  // For <script> tags inside template, the browser does NOT execute them on
  // append. We have to clone each script tag into a fresh element so it runs.
  const clone = document.createDocumentFragment();
  frag.childNodes.forEach((node) => {
    if (node.nodeName === "SCRIPT") {
      const s = document.createElement("script");
      Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.text = node.textContent;
      clone.appendChild(s);
    } else {
      clone.appendChild(node.cloneNode(true));
    }
  });
  container.appendChild(clone);
};

const TrackingScripts = ({ codes }) => {
  useEffect(() => {
    if (!codes || codes.enabled === false) {
      // Disabled — clear all three slots so an admin disable takes effect
      const head = document.getElementById(HEAD_MARKER);
      const bo = document.getElementById(BODY_OPEN_MARKER);
      const bc = document.getElementById(BODY_CLOSE_MARKER);
      [head, bo, bc].forEach((el) => { if (el) replaceContent(el, ""); });
      return;
    }
    const headEl = ensureContainer(document.head, HEAD_MARKER);
    const bodyOpenEl = ensureContainer(document.body, BODY_OPEN_MARKER, "prepend");
    const bodyCloseEl = ensureContainer(document.body, BODY_CLOSE_MARKER);

    replaceContent(headEl, codes.head_code || "");
    replaceContent(bodyOpenEl, codes.body_open_code || "");
    replaceContent(bodyCloseEl, codes.body_close_code || "");
  }, [codes?.enabled, codes?.head_code, codes?.body_open_code, codes?.body_close_code]);

  return null;
};

export default TrackingScripts;
